const CalculatorInfoType = Object.freeze({
  monthlyInstallments: 'Monthly Installments',
  downpayment: 'Downpayment',
  balloonPayment: 'Balloon Payment',
  aprRate: 'APR Rate',
  financeAmount: 'Finance Amount',
  managementFee: 'Management Fee',
  pfManagementFee: 'PF Standard Management Fee',
  insuranceCost: 'Insurance Cost',
  insuranceCostWithVAT: 'Insurance Cost + VAT',
  interestAmount: 'Interest Amount',
  totalFinanceAmount: 'Total Finance Amount',
  managementFeeWithVAT: 'Management + VAT',
  loanAmount: 'Loan Amount',
  totalProfit: 'Total Profit',
  SAR: 'SAR',
  years: 'Years',
  months: 'Months',
  percent: '%',
})

const APRate = (
  periods,
  payment,
  present,
  future = 0,
  type = 0,
  guess = 0.1,
) => {
  // Set maximum epsilon for end of iteration
  const epsMax = 1e-10

  // Set maximum number of iterations
  const iterMax = 10

  // Implement Newton's method
  let y = 0
  let y0 = 0
  let y1 = 0
  let x0 = 0
  let x1 = 0
  let f = 0
  let i = 0
  let rate = guess
  if (Math.abs(rate) < epsMax) {
    y =
      present * (1 + periods * rate) +
      payment * (1 + rate * type) * periods +
      future
  } else {
    f = Math.exp(periods * Math.log(1 + rate))
    y = present * f + payment * (1 / rate + type) * (f - 1) + future
  }
  y0 = present + payment * periods + future
  y1 = present * f + payment * (1 / rate + type) * (f - 1) + future
  i = 0
  x0 = 0
  x1 = rate
  while (Math.abs(y0 - y1) > epsMax && i < iterMax) {
    rate = (y1 * x0 - y0 * x1) / (y1 - y0)
    x0 = x1
    x1 = rate
    if (Math.abs(rate) < epsMax) {
      y =
        present * (1 + periods * rate) +
        payment * (1 + rate * type) * periods +
        future
    } else {
      f = Math.exp(periods * Math.log(1 + rate))
      y = present * f + payment * (1 / rate + type) * (f - 1) + future
    }
    y0 = y1
    y1 = y
    // eslint-disable-next-line no-plusplus
    ++i
  }
  return rate
}

const calculateInstantFinanceDetails = (financeAmount, tenure) => {
  const rules = {
    12: [
      { max: 5000, apr: 0.427, profit: 19.99 },
      { min: 5001, max: 10000, apr: 0.5179, profit: 19.99 },
      { min: 10001, max: 30000, apr: 0.4353, profit: 16.99 },
      { min: 30001, max: 40000, apr: 0.2788, profit: 10.99 },
      { min: 40001, max: 50000, apr: 0.2542, profit: 9.99 },
    ],
    24: [
      { max: 5000, apr: 0.427, profit: 19.99 },
      { min: 5001, max: 10000, apr: 0.4589, profit: 19.99 },
      { min: 10001, max: 30000, apr: 0.3894, profit: 16.99 },
      { min: 30001, max: 40000, apr: 0.2496, profit: 10.99 },
      { min: 40001, max: 50000, apr: 0.227, profit: 9.99 },
    ],
    36: [
      { min: 5000, max: 10000, apr: 0.427, profit: 19.99 },
      { min: 10001, max: 30000, apr: 0.3622, profit: 16.99 },
      { min: 30001, max: 40000, apr: 0.2358, profit: 10.99 },
      { min: 40001, max: 50000, apr: 0.2148, profit: 9.99 },
    ],
  }

  const matchingRule = rules[tenure]?.find(
    ({ min, max }) => financeAmount >= min && financeAmount <= max,
  )

  const { apr, profit } = matchingRule
  return { apr, profit }
}

const calculateStandardFinanceDetails = (financeAmount, tenure) => {
  const rules = {
    12: [
      { min: 51000, max: 300000, apr: 0.1819 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
    18: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 300000, apr: 0.1681 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
    24: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 300000, apr: 0.1608 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
    30: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 300000, apr: 0.1559 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
    36: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 300000, apr: 0.1522 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
    42: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 300000, apr: 0.1491 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
    48: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 300000, apr: 0.1461 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
    54: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 500000, apr: 0.144 },
    ],
    60: [
      { min: 51000, max: 51000, apr: 0.1819 },
      { min: 51001, max: 300000, apr: 0.1419 },
      { min: 300001, max: 500000, apr: 0.144 },
    ],
  }

  const matchingRule = rules[tenure]?.find(
    ({ min, max }) => financeAmount >= min && financeAmount <= max,
  )

  const { apr } = matchingRule

  return { apr }
}

const getFlexiMap = (tenure) => {
  switch (tenure) {
    case 2:
      return [0.34, 0.33, 0.33]
    case 5:
      return [0.2, ...Array(4).fill(1 / 8), 0.3]
    default:
      return Array(tenure + 1).fill(1 / (tenure + 1))
  }
}

const stringifyFiveNumbers = (number) => {
  const numberMap = {
    1: 'First',
    2: 'Second',
    3: 'Third',
    4: 'Fourth',
    5: 'Fifth',
  }
  return numberMap[number]
}

export const monthlyLeasingCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Car Price
    Down-payment
    Tenure Period
    Balloon Payment
    
    --- OUTPUT ---
    Finance Amount = Car Price - Down-payment
    Management Fee (Processing Fee) = Finance Amount * (0.1)
    Management Fee + VAT = Management Fee + (Management Fee * 0.15)
    Insurance Cost = (Car Price * Insurance %) * Tenure Period
    Interest Amount = ((Finance Amount * Profit Rate) * Tenor Period) / 12
    Total Finance Amount = Finance Amount + Insurance Cost + Interest Amount
    Monthly Installments = (Total Finance Amount - Balloon Payment) / Tenure Period
  */
  const insuranceRate = 0.04
  const profitRate = 0.04
  const onePercent = 0.01
  const addedVatToAmount = 1.15
  const {
    downpayment,
    'tenor-period': tenurePeriod,
    'balloon-payment': balloonPayment,
    'car-price': carPrice,
  } = calculatorValues

  const financeAmount = carPrice - downpayment
  const managementFee = financeAmount * onePercent
  const managementFeeWithVAT = managementFee * addedVatToAmount
  const insuranceCost =
    ((carPrice * insuranceRate * tenurePeriod) / 12) * addedVatToAmount
  const interestAmount = (financeAmount * profitRate * tenurePeriod) / 12
  const totalFinanceAmount = financeAmount + insuranceCost + interestAmount

  const monthlyInstallments =
    (totalFinanceAmount - balloonPayment) / tenurePeriod

  const rate = APRate(
    tenurePeriod,
    monthlyInstallments,
    -financeAmount + managementFeeWithVAT,
    downpayment,
  )

  const aprRate = ((rate + 1) ** 12 - 1) * 100

  return {
    mainResult: {
      title: CalculatorInfoType.monthlyInstallments,
      value: monthlyInstallments,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [
      {
        title: CalculatorInfoType.downpayment,
        value: downpayment,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.balloonPayment,
        value: balloonPayment,
        unit: CalculatorInfoType.SAR,
      },
    ],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFee,
        value: managementFee,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFeeWithVAT,
        value: managementFeeWithVAT,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.insuranceCostWithVAT,
        value: insuranceCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.aprRate,
        value: aprRate.toFixed(2),
        unit: CalculatorInfoType.percent,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const fiftyFiftyCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Car Price

    --- DEFAULT ---
    Tenure = 2 (Years)
    Insurance Rate = 0.02
    Profit Rate = 0.06

    --- OUTPUT ---
    Downpayment = Car Price * 0.5
    Balloon Payment = Car Price * 0.5
    Finance Amount = Car Price - Downpayment
    Insurance Cost = (Car Price * Insurance Rate * Tenure Period) / 12
    Interest Amount = (Finance Amount * Rate * Tenure Period) / 12
    Management Fee = Finance Amount * 0.01
    Management Fee With VAT = Management Fee * 1.05
    Total Finance Amount = Finance Amount + Insurance Cost + Interest Amount

    - Downpayment
    - APR 
    - Finance Amount
    - Balloon Payment
    - x Insurance Cost
    - x Interest amount 
    - x Total Finance Amount
    - Management Fee
    - Management Fee + VAT
    x Monthly Installments
  */
  const tenure = 24
  const insuranceRate = 0.04
  const profitRate = 0.04
  const { 'car-price': carPrice } = calculatorValues
  const addedVatToAmount = 1.15

  const downpayment = carPrice * 0.5
  const balloonPayment = downpayment
  const financeAmount = carPrice - downpayment
  const insuranceCost =
    ((carPrice * insuranceRate * tenure) / 12) * addedVatToAmount
  const interestAmount = (financeAmount * profitRate * tenure) / 12
  const managementFee = financeAmount * 0.01
  const managementFeeWithVAT = managementFee * addedVatToAmount
  const totalFinanceAmount = financeAmount + interestAmount + insuranceCost
  const monthlyInstallments = (totalFinanceAmount - balloonPayment) / tenure

  const rate = APRate(
    tenure,
    monthlyInstallments,
    -financeAmount + managementFeeWithVAT,
    balloonPayment,
  )
  const aprRate = ((rate + 1) ** 12 - 1) * 100

  return {
    mainResult: {
      title: CalculatorInfoType.downpayment,
      value: downpayment,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [
      {
        title: CalculatorInfoType.balloonPayment,
        value: balloonPayment,
        unit: CalculatorInfoType.SAR,
      },
    ],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.monthlyInstallments,
        value: monthlyInstallments,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.insuranceCostWithVAT,
        value: insuranceCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.aprRate,
        value: aprRate.toFixed(2),
        unit: CalculatorInfoType.percent,
      },
      {
        title: CalculatorInfoType.managementFee,
        value: managementFee,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFeeWithVAT,
        value: managementFeeWithVAT,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const flexiCalculation = (calculatorValues) => {
  /* 
    --- INPUT ---
    Car Price
    Tenure Period

    --- DEFAULT ---
    Insurance Rate = 0.0453
    Flat Rate = 0.0499

    --- OUTPUT ---
    Loan Amount = Sum(1 to n-1) of Flexi Data
    Total Profit = Loan Amount * Flat Rate * Tenure Period
    Insurance = Insurance Rate * Car Price * Tenure Period
    Installment = (Loan Amount + Total Profit + Insurance) / Tenure Period
    Fees = Loan Amount * 0.1
    Fees + VAT = Fees * 1.15
  */

  const insuranceRate = 0.04
  const flatRate = 0.04
  const { 'car-price': carPrice, 'tenor-period': tenure } = calculatorValues
  const addedVatToAmount = 1.15

  const flexiMap = getFlexiMap(tenure)
  const flexiData = flexiMap.map((flexiAmount, year) => {
    let eyebrow = year
    let label = `${stringifyFiveNumbers(year)} Payment`
    if (year === 0) {
      label = 'Downpayment'
      eyebrow = null
    }
    if (year === flexiMap.length - 1) {
      label = 'Final Payment'
    }
    return {
      label,
      eyebrow,
      value: flexiAmount * carPrice,
      unit: CalculatorInfoType.SAR,
    }
  })

  const loanAmount = [...flexiData].splice(1).reduce((a, c) => a + c.value, 0)
  const totalProfit = loanAmount * flatRate * tenure
  const insuranceCost = carPrice * insuranceRate * tenure * addedVatToAmount
  const tenureInMonths = tenure * 12
  const monthlyInstallments = (totalProfit + insuranceCost) / tenureInMonths
  const managementFee = loanAmount * 0.01
  const managementFeeWithVAT = managementFee * addedVatToAmount

  return {
    mainResult: {},
    subResults: flexiData,
    generalResult: [
      {
        title: CalculatorInfoType.loanAmount,
        value: loanAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.totalProfit,
        value: totalProfit,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.insuranceCostWithVAT,
        value: insuranceCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.monthlyInstallments,
        value: monthlyInstallments,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFee,
        value: managementFee,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFeeWithVAT,
        value: managementFeeWithVAT,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const signAndDriveCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Car Price

    --- DEFAULT ---
    Tenure = 2 (Years)
    Insurance Rate = 0.02
    Profit Rate = 0.06

    --- OUTPUT ---
    Downpayment = Car Price * 0.5
    Balloon Payment = Car Price * 0.5
    Finance Amount = Car Price - Downpayment
    Insurance Cost = Car Price * Insurance Rate * Tenure Period
    Interest Cost = Finance Amount * Rate * Tenure Period
    Management Fee = Finance Amount * 0.01
    Total Finance Amount = Finance Amount + Interest Cost + Insurance Cost
  */
  const tenure = 2
  const insuranceRate = 0.02
  const profitRate = 0.05
  const { 'car-price': carPrice } = calculatorValues

  const downpayment = carPrice * 0.5
  const balloonPayment = downpayment
  const financeAmount = carPrice - downpayment
  const insuranceCost = carPrice * insuranceRate * tenure
  const interestCost = financeAmount * profitRate * tenure
  const managementFee = financeAmount * 0.01
  const totalFinanceAmount =
    financeAmount + interestCost + insuranceCost + managementFee

  return {
    mainResult: {
      title: CalculatorInfoType.downpayment,
      value: downpayment,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [
      {
        title: CalculatorInfoType.balloonPayment,
        value: balloonPayment,
        unit: CalculatorInfoType.SAR,
      },
    ],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFee,
        value: managementFee,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.insuranceCostWithVAT,
        value: insuranceCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const salesAndLeaseBackCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Car Price

    --- DEFAULT ---
    Tenure = 2 (Years)
    Insurance Rate = 0.02
    Profit Rate = 0.06

    --- OUTPUT ---
    Balloon Payment = Car Price * Balloon Rate
    Finance Amount = Car Price * 0.8 - Balloon Payment
    Management Fee = Finance Amount * 0.1
    Management Fee + VAT = Management Fee * 1.15
    Insurance Cost = (Car Price * Insurance Rate * Tenure Period) / 12
    Interest Amount = (Finance Amount * Rate * Tenure Period) / 12
    Total Finance Amount = Finance Amount + Interest Amount + Insurance Cost
    Monthly Installments = (Total Finance Amount - Balloon Payment) / Tenure Period
  */
  const insuranceRate = 0.01
  const profitRate = 0.01
  const {
    'car-price': carPrice,
    'tenor-period': tenure,
    'balloon-payment': balloonRate,
  } = calculatorValues

  const balloonPayment = carPrice * balloonRate
  const financeAmount = carPrice * 0.8 - balloonPayment
  const managementFee = financeAmount * 0.01
  const managementFeeWithVAT = managementFee * 1.15
  const insuranceCost = (carPrice * insuranceRate * tenure) / 12
  const interestAmount = (financeAmount * profitRate * tenure) / 12
  const totalFinanceAmount = financeAmount + interestAmount + insuranceCost
  const monthlyInstallments = (totalFinanceAmount - balloonPayment) / tenure

  const rate = APRate(
    tenure,
    monthlyInstallments,
    -financeAmount + managementFeeWithVAT,
    balloonPayment,
  )
  const aprRate = ((rate + 1) ** 12 - 1) * 100

  return {
    mainResult: {
      title: CalculatorInfoType.monthlyInstallments,
      value: monthlyInstallments,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [
      {
        title: CalculatorInfoType.balloonPayment,
        value: balloonPayment,
        unit: CalculatorInfoType.SAR,
      },
    ],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.aprRate,
        value: aprRate.toFixed(2),
        unit: CalculatorInfoType.percent,
      },
      {
        title: CalculatorInfoType.managementFee,
        value: managementFee,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFeeWithVAT,
        value: managementFeeWithVAT,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.insuranceCostWithVAT,
        value: insuranceCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const motorcycleCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Car Price

    --- DEFAULT ---
    Tenure = 2 (Years)
    Insurance Rate = 0.02
    Profit Rate = 0.06

    --- OUTPUT ---
    Downpayment = Motorcycle Price * 0.1
    Balloon Payment  = Motorcycle Price * 0.2
    Finance Amount = Motorcycle Price - Downpayment
    Insurance Cost = (Motorcycle Price * Insurance Rate * Tenure Period) / 12
    Interest Amount = (Finance Amount * Rate * Tenure Period) / 12
    Total Finance Amount = Finance Amount + Interest Cost + Insurance Cost
    Management Fee = Finance Amount * 0.01
    Management Fee With VAT = Management Fee * 1.15
    Monthly Installments = (Total Finance Amount - Balloon Payment) / Tenure Period
  */
  const insuranceRate = 0.02
  const profitRate = 0.02
  const downpaymentRate = 0.1
  const balloonRate = 0.1
  const {
    'motorcycle-price': motorcyclePrice,
    'tenor-period': tenure,
  } = calculatorValues

  const downpayment = motorcyclePrice * downpaymentRate
  const balloonPayment = motorcyclePrice * balloonRate
  const financeAmount = motorcyclePrice - downpayment
  const insuranceCost = (motorcyclePrice * insuranceRate * tenure) / 12
  const interestCost = (financeAmount * profitRate * tenure) / 12
  const managementFee = financeAmount * 0.01
  const managementFeeWithVAT = managementFee * 1.15
  const totalFinanceAmount = financeAmount + interestCost + insuranceCost
  const monthlyInstallments = (totalFinanceAmount - balloonPayment) / tenure

  const rate = APRate(
    tenure,
    monthlyInstallments,
    -financeAmount + managementFeeWithVAT,
    downpayment,
  )
  const aprRate = ((rate + 1) ** 12 - 1) * 100

  return {
    mainResult: {
      title: CalculatorInfoType.downpayment,
      value: downpayment,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [
      {
        title: CalculatorInfoType.balloonPayment,
        value: balloonPayment,
        unit: CalculatorInfoType.SAR,
      },
    ],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.monthlyInstallments,
        value: monthlyInstallments,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.aprRate,
        value: aprRate.toFixed(2),
        unit: CalculatorInfoType.percent,
      },
      {
        title: CalculatorInfoType.insuranceCostWithVAT,
        value: insuranceCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFee,
        value: managementFee,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFeeWithVAT,
        value: managementFeeWithVAT,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const takhayerCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Car Price

    --- DEFAULT ---
    Tenure = 2 (Years)
    Insurance Rate = 0.02
    Profit Rate = 0.06

    --- OUTPUT ---
    Downpayment = Car Price * 0.4
    Finance Amount = Car Price - Downpayment
    Insurance Cost = (Car Price * Insurance Rate * Tenure Period) / 12
    Interest Amount = (Finance Amount * Rate * Tenure Period) / 12
    Total Finance Amount = Finance Amount + Interest Cost + Insurance Cost
    Management Fee = Finance Amount * 0.01
    Management Fee With VAT = Management Fee * 1.15
    Monthly Installments = Interest Cost + Insurance Cost / Tenure Period
  */
  const tenure = 24
  const insuranceRate = 0.04
  const profitRate = 0.04
  const addedVatToAmount = 1.15
  const {
    'car-price': carPrice,
    'takhayer-rate': downpaymentRate,
  } = calculatorValues

  const downpayment = carPrice * (downpaymentRate / 100)
  const financeAmount = carPrice - downpayment
  const insuranceCost =
    ((carPrice * insuranceRate * tenure) / 12) * addedVatToAmount
  const interestCost = (financeAmount * profitRate * tenure) / 12
  const managementFee = financeAmount * 0.01
  const managementFeeWithVAT = managementFee * 1.15
  const totalFinanceAmount = financeAmount + interestCost + insuranceCost
  const monthlyInstallments = (interestCost + insuranceCost) / tenure
  const balloonPayment = financeAmount

  const rate = APRate(
    tenure,
    monthlyInstallments,
    -financeAmount + managementFeeWithVAT,
    financeAmount,
  )

  const aprRate = ((rate + 1) ** 12 - 1) * 100

  return {
    mainResult: {
      title: CalculatorInfoType.downpayment,
      value: downpayment,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.insuranceCostWithVAT,
        value: insuranceCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.balloonPayment,
        value: balloonPayment,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.aprRate,
        value: aprRate.toFixed(2),
        unit: CalculatorInfoType.percent,
      },
      {
        title: CalculatorInfoType.monthlyInstallments,
        value: monthlyInstallments,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestCost,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFee,
        value: managementFee,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.managementFeeWithVAT,
        value: managementFeeWithVAT,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const personalFinanceStandardCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Finance Amount
    Tenure Period

    --- DEFAULT ---
    Profit Rate = 0.05

    --- OUTPUT ---
    Interest Rate = Profit Rate * Tenure Period * Finance Amount
    Management Fee = Finance Amount * 0.01
    Management Fee + VAT = Management Fee * 1.05
    Total Finance Amount = Finance Amount + Interest Rate + Management Fee + VAT
    Monthly Installment = Total Finance Amount / Tenure Period
  */
  const profitRate = 0.0699
  const {
    'finance-amount': financeAmount,
    'tenor-period': tenure,
  } = calculatorValues

  const interestAmount = (profitRate * tenure * financeAmount) / 12
  const totalFinanceAmount = financeAmount + interestAmount
  const monthlyInstallment = totalFinanceAmount / tenure
  const { apr } = calculateStandardFinanceDetails(financeAmount, tenure)

  const aprRate = apr * 100

  return {
    mainResult: {
      title: CalculatorInfoType.monthlyInstallments,
      value: monthlyInstallment,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.aprRate,
        value: aprRate.toFixed(2),
        unit: CalculatorInfoType.percent,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}

export const personalFinanceInstantCalculation = (calculatorValues) => {
  /*
    --- INPUT ---
    Finance Amount
    Tenure Period

    --- DEFAULT ---
    Profit Rate = 0.05

    --- OUTPUT ---
    Interest Rate = Profit Rate * Tenure Period * Finance Amount
    Management Fee = Finance Amount * 0.01
    Management Fee + VAT = Management Fee * 1.05
    Total Finance Amount = Finance Amount + Interest Rate + Management Fee + VAT
    Monthly Installment = Total Finance Amount / Tenure Period
  */
  const {
    'finance-amount': financeAmount,
    'tenor-period': tenure,
  } = calculatorValues

  const { apr, profit } = calculateInstantFinanceDetails(financeAmount, tenure)

  const aprRate = apr * 100

  const interestAmount = (profit * financeAmount * tenure) / (100 * 12)
  const totalFinanceAmount = financeAmount + interestAmount
  const monthlyInstallment = totalFinanceAmount / tenure

  return {
    mainResult: {
      title: CalculatorInfoType.monthlyInstallments,
      value: monthlyInstallment,
      unit: CalculatorInfoType.SAR,
    },
    subResults: [],
    generalResult: [
      {
        title: CalculatorInfoType.financeAmount,
        value: financeAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.interestAmount,
        value: interestAmount,
        unit: CalculatorInfoType.SAR,
      },
      {
        title: CalculatorInfoType.aprRate,
        value: aprRate.toFixed(2),
        unit: CalculatorInfoType.percent,
      },
      {
        title: CalculatorInfoType.totalFinanceAmount,
        value: totalFinanceAmount,
        unit: CalculatorInfoType.SAR,
      },
    ],
  }
}
